import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  ChevronRight, ChevronLeft, MapPin, Ruler, Scissors, Check, 
  ExternalLink, HelpCircle, Info, Mail, User, Lock, Phone, Home
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import GooglePlacesAutocomplete from "./GooglePlacesAutocomplete";
import { triggerMow } from "./MowerOverlay";

const API = "/api";

const GRASS_HEIGHT_IMAGES = {
  short: "https://images.unsplash.com/photo-1646066430000-b3a4d428a602?w=400&q=80",
  medium: "https://images.unsplash.com/photo-1558904541-efa843a96f01?w=400&q=80",
  tall: "https://images.unsplash.com/photo-1588656930914-535cfb358338?w=400&q=80",
  overgrown: "https://images.unsplash.com/photo-1717520867957-fe0496d6461e?w=400&q=80",
};

const UNKNOWN_SHRUG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/e3adbda9-f4db-46cf-a8ca-cf2d8ccdb7ef/images/83fafae31dc1da8f83fb10a84e7ec00d04eaca12b0287b7540607e5c422290be.png";

const BookingWizard = ({ isOpen, onClose, defaultAddress = "" }) => {
  const { initiateGoogleLogin, user } = useAuth();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [showMeasureHelp, setShowMeasureHelp] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    yardSize: "",
    grassHeight: "",
  });
  const [emailSignup, setEmailSignup] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });

  // If user is already logged in, start at step 2 (skip signup)
  const startStep = user ? 2 : 1;
  const totalSteps = user ? 4 : 5;
  const lastStep = 5; // Always 5 regardless of login state

  // Reset to appropriate starting step when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(startStep);
      setShowMeasureHelp(false);
      setShowEmailForm(false);
      if (defaultAddress) setFormData((f) => (f.address ? f : { ...f, address: defaultAddress }));
    }
  }, [isOpen, startStep, defaultAddress]);

  const yardSizes = [
    { id: "small", label: t("booking.yardSize.small", "0 - 100 m²"), description: t("booking.yardSizeDesc.small", "Small yard"), icon: "🏠" },
    { id: "medium", label: t("booking.yardSize.medium", "100 - 300 m²"), description: t("booking.yardSizeDesc.medium", "Medium yard"), icon: "🏡" },
    { id: "large", label: t("booking.yardSize.large", "300 - 500 m²"), description: t("booking.yardSizeDesc.large", "Large yard"), icon: "🏘️" },
    { id: "xlarge", label: t("booking.yardSize.xlarge", "500+ m²"), description: t("booking.yardSizeDesc.xlarge", "Extra large"), icon: "🌳" },
    { id: "unknown", label: t("booking.yardSize.unknown", "Unknown"), description: t("booking.yardSizeDesc.unknown", "Not sure of yard size"), icon: null, image: UNKNOWN_SHRUG_IMAGE },
  ];

  const grassHeights = [
    { 
      id: "short", 
      label: t("booking.grassHeight.short", "Short (< 5cm)"), 
      description: t("booking.grassHeightDesc.short", "Recently cut, just needs maintenance"),
      image: GRASS_HEIGHT_IMAGES.short 
    },
    { 
      id: "medium", 
      label: t("booking.grassHeight.medium", "Medium (5-15cm)"), 
      description: t("booking.grassHeightDesc.medium", "Slightly overgrown, standard cut needed"),
      image: GRASS_HEIGHT_IMAGES.medium 
    },
    { 
      id: "tall", 
      label: t("booking.grassHeight.tall", "Tall (15-30cm)"), 
      description: t("booking.grassHeightDesc.tall", "Overgrown, requires more work"),
      image: GRASS_HEIGHT_IMAGES.tall 
    },
    { 
      id: "overgrown", 
      label: t("booking.grassHeight.overgrown", "Very Tall (30cm+)"), 
      description: t("booking.grassHeightDesc.overgrown", "Heavily overgrown, may need multiple passes"),
      image: GRASS_HEIGHT_IMAGES.overgrown 
    },
    { 
      id: "unknown", 
      label: t("booking.grassHeight.unknown", "Unknown"), 
      description: t("booking.grassHeightDesc.unknown", "Not sure of grass height"),
      image: UNKNOWN_SHRUG_IMAGE 
    },
  ];

  const handleGoogleSignIn = () => {
    localStorage.setItem("bookingStep", "2");
    localStorage.setItem("bookingData", JSON.stringify(formData));
    initiateGoogleLogin("customer");
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    
    if (!emailSignup.name || !emailSignup.email || !emailSignup.phone || !emailSignup.password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (emailSignup.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/auth/email-signup`, {
        name: emailSignup.name,
        email: emailSignup.email,
        phone: emailSignup.phone,
        password: emailSignup.password,
      }, { withCredentials: true });

      toast.success("Account created successfully!");
      localStorage.setItem("bookingData", JSON.stringify(formData));
      setStep(2); // Move to next step
    } catch (error) {
      console.error("Signup failed:", error);
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed to create account. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (step < lastStep) {
      setStep(step + 1);
    } else {
      // Submit quote to backend
      setIsSubmitting(true);
      try {
        const quoteData = {
          customer_name: user?.name || emailSignup.name,
          customer_email: user?.email || emailSignup.email,
          customer_phone: user?.phone || emailSignup.phone || "",
          customer_address: formData.address,
          yard_size: formData.yardSize,
          grass_height: formData.grassHeight,
        };
        
        await axios.post(`${API}/quotes`, quoteData, { withCredentials: true });
        
        toast.success("Quote request submitted! We'll contact you soon with pricing.");
        triggerMow();
        resetAndClose();
      } catch (error) {
        console.error("Failed to submit quote:", error);
        toast.error("Failed to submit quote. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    if (step > startStep) {
      setStep(step - 1);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return false; // Signup step - handled by buttons
      case 2:
        return formData.address.trim() !== ""; // Address step
      case 3:
        return true; // Measure step - always can proceed
      case 4:
        return formData.yardSize !== "";
      case 5:
        return formData.grassHeight !== "";
      default:
        return false;
    }
  };

  const resetAndClose = () => {
    setStep(startStep);
    setFormData({ address: "", yardSize: "", grassHeight: "" });
    setEmailSignup({ name: "", email: "", phone: "", password: "" });
    setShowEmailForm(false);
    onClose();
  };

  const handleSkipToQuote = async () => {
    setIsSubmitting(true);
    try {
      const quoteData = {
        customer_name: user?.name || emailSignup.name,
        customer_email: user?.email || emailSignup.email,
        customer_phone: user?.phone || emailSignup.phone || "",
        customer_address: formData.address,
        yard_size: formData.yardSize || "unknown",
        grass_height: formData.grassHeight || "unknown",
      };
      await axios.post(`${API}/quotes`, quoteData, { withCredentials: true });
      toast.success("Quote request submitted! We'll contact you soon with pricing.");
      triggerMow();
      resetAndClose();
    } catch (error) {
      console.error("Failed to submit quote:", error);
      toast.error("Failed to submit quote. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dynamic step labels based on whether user is logged in
  const allStepLabels = [t("booking.step1.title", "Sign Up"), t("booking.step2.title", "Address"), t("booking.measureStep", "Measure"), t("booking.step3.title", "Yard Size"), t("booking.step4.title", "Grass Height")];
  const stepLabels = user ? allStepLabels.slice(1) : allStepLabels;
  const displaySteps = user ? [2, 3, 4, 5] : [1, 2, 3, 4, 5];
  const currentDisplayStep = user ? step - 1 : step;

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase text-center">
            {t("booking.title", "Get Your Quote")}
          </DialogTitle>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            {displaySteps.map((s, idx) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                  s < step 
                    ? "bg-[#39FF14] text-black" 
                    : s === step 
                    ? "bg-[#39FF14]/20 border-2 border-[#39FF14] text-[#39FF14]" 
                    : "bg-[#27272A] text-[#A1A1AA]"
                }`}>
                  {s < step ? <Check className="w-4 h-4" /> : idx + 1}
                </div>
                {idx < displaySteps.length - 1 && (
                  <div className={`w-8 sm:w-16 h-1 mx-1 sm:mx-2 ${
                    s < step ? "bg-[#39FF14]" : "bg-[#27272A]"
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-[#A1A1AA]">
            {stepLabels.map((label, i) => (
              <span key={i} className="text-center w-16">{label}</span>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="min-h-[300px]">
          {/* Step 1: Sign Up Options */}
          {step === 1 && (
            <div className="animate-fade-in">
              {!showEmailForm ? (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-[#39FF14]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-[#39FF14]" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{t("booking.signUpTitle", "Sign Up to Get Started")}</h3>
                    <p className="text-[#A1A1AA]">
                      {t("booking.signUpDesc", "Create an account to save your quote and receive updates")}
                    </p>
                  </div>

                  <div className="space-y-3 mb-6">
                    {/* Google Sign In */}
                    <Button
                      data-testid="google-signup-btn"
                      onClick={handleGoogleSignIn}
                      className="w-full bg-white text-black hover:bg-gray-100 font-semibold py-6 rounded-full flex items-center justify-center gap-3"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {t("auth.continueWithGoogle", "Continue with Google")}
                    </Button>

                    {/* Apple Sign In */}
                    <Button
                      data-testid="apple-signup-btn"
                      onClick={handleGoogleSignIn}
                      className="w-full bg-black text-white border border-white/20 hover:bg-gray-900 font-semibold py-6 rounded-full flex items-center justify-center gap-3"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      {t("auth.continueWithApple", "Continue with Apple")}
                    </Button>

                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[#27272A]"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-[#0A0A0A] text-[#A1A1AA]">{t("common.or", "or")}</span>
                      </div>
                    </div>

                    {/* Email Sign Up */}
                    <Button
                      data-testid="email-signup-btn"
                      onClick={() => setShowEmailForm(true)}
                      variant="outline"
                      className="w-full border-[#27272A] text-white hover:bg-[#27272A] font-semibold py-6 rounded-full flex items-center justify-center gap-3"
                    >
                      <Mail className="w-5 h-5" />
                      {t("auth.signInWithEmail", "Sign up with Email")}
                    </Button>
                  </div>

                  <div className="bg-[#121212] border border-[#27272A] rounded-xl p-4">
                    <p className="text-[#A1A1AA] text-sm text-center">
                      {t("auth.worksWithAnyEmail", "Works with any email: Gmail, iCloud, Yahoo, Outlook, and more")}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold mb-2">{t("auth.createAccount", "Create Your Account")}</h3>
                    <p className="text-[#A1A1AA]">
                      {t("auth.enterDetails", "Enter your details to get started")}
                    </p>
                  </div>

                  <form onSubmit={handleEmailSignup} className="space-y-4">
                    <div>
                      <Label className="text-[#A1A1AA]">{t("auth.fullName", "Full Name")}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                        <Input
                          data-testid="signup-name-input"
                          value={emailSignup.name}
                          onChange={(e) => setEmailSignup({ ...emailSignup, name: e.target.value })}
                          className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]"
                          placeholder="John Doe"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-[#A1A1AA]">{t("auth.email", "Email Address")}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                        <Input
                          data-testid="signup-email-input"
                          type="email"
                          value={emailSignup.email}
                          onChange={(e) => setEmailSignup({ ...emailSignup, email: e.target.value })}
                          className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]"
                          placeholder="you@example.com"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-[#A1A1AA]">{t("staff.phone", "Phone Number")}</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                        <Input
                          data-testid="signup-phone-input"
                          type="tel"
                          value={emailSignup.phone}
                          onChange={(e) => setEmailSignup({ ...emailSignup, phone: e.target.value })}
                          className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]"
                          placeholder="+81 90-1234-5678"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-[#A1A1AA]">{t("auth.password", "Password")}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                        <Input
                          data-testid="signup-password-input"
                          type="password"
                          value={emailSignup.password}
                          onChange={(e) => setEmailSignup({ ...emailSignup, password: e.target.value })}
                          className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]"
                          placeholder={t("auth.minChars", "Min. 6 characters")}
                          required
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      data-testid="create-account-btn"
                      disabled={isSubmitting}
                      className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold py-6 rounded-full"
                    >
                      {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      ) : (
                        t("auth.createAccount", "Create Account")
                      )}
                    </Button>

                    <button
                      type="button"
                      onClick={() => setShowEmailForm(false)}
                      className="w-full text-[#A1A1AA] text-sm hover:text-white transition-colors"
                    >
                      ← {t("common.back", "Back to sign up options")}
                    </button>
                  </form>
                </>
              )}

              <p className="text-center text-[#A1A1AA] text-xs mt-4">
                {t("auth.terms", "By signing up, you agree to our terms of service")}
              </p>
            </div>
          )}

          {/* Step 2: Address Entry */}
          {step === 2 && (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#39FF14]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Home className="w-8 h-8 text-[#39FF14]" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{t("booking.step2.title", "Where's Your Property?")}</h3>
                <p className="text-[#A1A1AA]">
                  {t("booking.step2.subtitle", "Enter your address so we can find your location")}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-[#A1A1AA] mb-2 block">{t("booking.propertyAddress", "Property Address")}</Label>
                  <GooglePlacesAutocomplete
                    value={formData.address}
                    onChange={(address) => setFormData({ ...formData, address })}
                    placeholder={t("booking.addressPlaceholder", "Start typing your address...")}
                    className="h-12"
                  />
                  <p className="text-xs text-[#A1A1AA] mt-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {t("booking.addressHint", "Start typing and select from suggestions")}
                  </p>
                </div>

                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-4">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-[#39FF14]" />
                    {t("booking.cantFindAddress", "Can't find your address?")}
                  </h4>
                  <p className="text-[#A1A1AA] text-sm mb-3">
                    {t("booking.manualAddressHint", "You can also type your full address manually if it doesn't appear in the suggestions.")}
                  </p>
                  <Textarea
                    data-testid="manual-address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14] min-h-[80px]"
                    placeholder={t("booking.manualAddressPlaceholder", "Example: Kadena AFB Housing 4732 B")}
                  />
                </div>

                <a 
                  href="https://maps.google.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full p-3 bg-[#064E3B] hover:bg-[#065F46] rounded-xl text-[#39FF14] font-semibold transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t("booking.openGoogleMaps", "Open Google Maps")}
                </a>
              </div>
            </div>
          )}

          {/* Step 3: How to Measure */}
          {step === 3 && (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#39FF14]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Ruler className="w-8 h-8 text-[#39FF14]" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{t("booking.measureTitle", "Let's Measure Your Yard")}</h3>
                <p className="text-[#A1A1AA]">
                  {t("booking.measureDesc", "Don't know your yard size? Use Google Earth to measure it easily!")}
                </p>
              </div>

              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6 mb-4">
                <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#39FF14]" />
                  {t("booking.howToMeasure", "How to Measure with Google Earth")}
                </h4>
                <ol className="space-y-3 text-[#A1A1AA] text-sm">
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#39FF14] text-black rounded-full flex items-center justify-center font-bold text-xs shrink-0">1</span>
                    <span>{t("booking.measureStep1", "Go to")} <a href="https://earth.google.com" target="_blank" rel="noopener noreferrer" className="text-[#39FF14] hover:underline">earth.google.com</a> {t("booking.measureStep1b", "and search for your address")}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#39FF14] text-black rounded-full flex items-center justify-center font-bold text-xs shrink-0">2</span>
                    <span>{t("booking.measureStep2", "Click the ruler icon")} <Ruler className="w-4 h-4 inline text-[#39FF14]" /> {t("booking.measureStep2b", "in the top toolbar")}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#39FF14] text-black rounded-full flex items-center justify-center font-bold text-xs shrink-0">3</span>
                    <span>{t("booking.measureStep3", "Click around the edges of your lawn to draw the area")}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#39FF14] text-black rounded-full flex items-center justify-center font-bold text-xs shrink-0">4</span>
                    <span>{t("booking.measureStep4", "The area will be displayed - note down the square meters")}</span>
                  </li>
                </ol>
              </div>

              <a 
                href="https://earth.google.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full p-4 bg-[#064E3B] hover:bg-[#065F46] rounded-xl text-[#39FF14] font-semibold transition-colors"
              >
                <ExternalLink className="w-5 h-5" />
                {t("booking.openGoogleEarth", "Open Google Earth")}
              </a>

              <p className="text-center text-[#A1A1AA] text-sm mt-4">
                {t("booking.alreadyKnowSize", "Already know your yard size? Click Next to continue")}
              </p>
            </div>
          )}

          {/* Step 4: Yard Size Selection */}
          {step === 4 && (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold mb-2">{t("booking.step3.title", "What's Your Yard Size?")}</h3>
                <p className="text-[#A1A1AA]">{t("booking.step3.subtitle", "Select the option that best matches your lawn area")}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {yardSizes.map((size) => (
                  <button
                    key={size.id}
                    data-testid={`yard-size-${size.id}`}
                    onClick={() => setFormData({ ...formData, yardSize: size.id })}
                    className={`p-6 rounded-xl border-2 transition-all text-left ${
                      formData.yardSize === size.id
                        ? "border-[#39FF14] bg-[#39FF14]/10 shadow-[0_0_15px_rgba(57,255,20,0.2)]"
                        : "border-[#27272A] bg-[#121212] hover:border-[#39FF14]/50"
                    }`}
                  >
                    {size.image ? (
                      <img src={size.image} alt={size.label} className="w-12 h-12 object-contain mb-2 rounded-lg" />
                    ) : (
                      <div className="text-3xl mb-2">{size.icon}</div>
                    )}
                    <h4 className="font-semibold text-white mb-1">{size.label}</h4>
                    <p className="text-[#A1A1AA] text-sm">{size.description}</p>
                    {formData.yardSize === size.id && (
                      <div className="mt-2 flex items-center gap-1 text-[#39FF14] text-sm">
                        <Check className="w-4 h-4" /> {t("common.selected", "Selected")}
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <button 
                onClick={() => setShowMeasureHelp(!showMeasureHelp)}
                className="flex items-center gap-2 text-[#A1A1AA] text-sm mt-4 hover:text-[#39FF14] transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                {t("booking.notSureSize", "Not sure about your yard size?")}
              </button>

              {showMeasureHelp && (
                <div className="mt-2 p-4 bg-[#121212] border border-[#27272A] rounded-lg text-sm text-[#A1A1AA]">
                  <p className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-[#39FF14] mt-0.5" />
                    {t("booking.measureHelpTip", "Use Google Earth to measure. Go back to Step 2 for instructions, or estimate based on how long it typically takes to mow (small = 15min, medium = 30min, large = 45min+)")}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Grass Height Selection */}
          {step === 5 && (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold mb-2">{t("booking.step4.title", "Current Grass Height?")}</h3>
                <p className="text-[#A1A1AA]">{t("booking.step4.subtitle", "Select the image that best matches your lawn")}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {grassHeights.map((height) => (
                  <button
                    key={height.id}
                    data-testid={`grass-height-${height.id}`}
                    onClick={() => setFormData({ ...formData, grassHeight: height.id })}
                    className={`rounded-xl border-2 overflow-hidden transition-all ${
                      formData.grassHeight === height.id
                        ? "border-[#39FF14] shadow-[0_0_15px_rgba(57,255,20,0.2)]"
                        : "border-[#27272A] hover:border-[#39FF14]/50"
                    }`}
                  >
                    <div className="relative h-32 overflow-hidden">
                      <img 
                        src={height.image} 
                        alt={height.label}
                        className="w-full h-full object-cover"
                      />
                      {formData.grassHeight === height.id && (
                        <div className="absolute inset-0 bg-[#39FF14]/20 flex items-center justify-center">
                          <div className="w-10 h-10 bg-[#39FF14] rounded-full flex items-center justify-center">
                            <Check className="w-6 h-6 text-black" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-3 bg-[#121212]">
                      <h4 className="font-semibold text-white text-sm mb-1">{height.label}</h4>
                      <p className="text-[#A1A1AA] text-xs">{height.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t border-[#27272A]">
          <Button
            variant="outline"
            onClick={step === 1 ? resetAndClose : handleBack}
            className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            {step === 1 ? t("common.cancel", "Cancel") : t("common.back", "Back")}
          </Button>

          <div className="flex items-center gap-3">
            {step > 2 && step < lastStep && (
              <Button
                variant="ghost"
                onClick={handleSkipToQuote}
                disabled={isSubmitting}
                className="text-[#A1A1AA] hover:text-white hover:bg-[#27272A] text-sm"
                data-testid="skip-to-quote-btn"
              >
                <Scissors className="w-4 h-4 mr-1" />
                {isSubmitting ? "Submitting..." : t("booking.skipToQuote", "SKIP TO QUOTE")}
              </Button>
            )}

            {step === 1 ? (
              <div />
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`font-bold rounded-full px-8 transition-all ${
                  canProceed()
                    ? "bg-[#39FF14] text-black hover:bg-[#32D711] hover:shadow-[0_0_15px_rgba(57,255,20,0.6)]"
                    : "bg-[#27272A] text-[#A1A1AA] cursor-not-allowed"
                }`}
              >
                {step === lastStep ? (
                  <>
                    {t("booking.submitQuote", "Get Quote")}
                    <Scissors className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    {t("common.next", "Next")}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingWizard;
